<template>
    <!-- timeline -->
    <div class="timeline-wrapper">
        <div class="timeline-component-pc">
            <div class="timeline-progress">
                <div class="timeline-progress-bar" />
            </div>
            <div
                v-for="(item, index) in timelineData"
                :key="index"
                :class="{ 'timeline-item': true, 'timeline-item-active': index === curTimelineIndex }"
            >
                <div class="timeline-left" style="will-change: opacity; opacity: 1">
                    <div class="sticky-block">
                        <div class="timeline-title">
                            {{ $t(item.title) }}
                        </div>
                    </div>
                </div>
                <div class="timeline-centre">
                    <div class="timeline-circle" />
                </div>
                <div class="timeline-right fabric-fade-in" style="will-change: opacity; opacity: 1">
                    <CommonComponentsVideoPlayer
                        class="fabric-timeline-video-elmt"
                        :sources="[
                            {
                                src: item.mediaPath,
                                type: 'video/mp4',
                            },
                        ]"
                        :poster="item.poster"
                        :autoplay="false"
                    />
                </div>
            </div>
        </div>
        <div class="timeline-component-mobile">
            <CommonComponentsTimeLine>
                <template #content>
                    <div
                        v-for="(item, index) in timelineData"
                        :key="index"
                        :class="{
                            'timeline-mobile-item': true,
                            'timeline-mobile-item-active': index === curTimelineIndex,
                        }"
                    >
                        <div class="timeline-mobile-item-title">
                            {{ $t(item.title) }}
                        </div>
                        <div class="timeline-mobile-item-media fabric-fade-in">
                            <CommonComponentsVideoPlayer
                                class="fabric-timeline-video-elm-mobile"
                                :sources="[
                                    {
                                        src: item.mediaPath,
                                        type: 'video/mp4',
                                    },
                                ]"
                                :poster="item.poster"
                            />
                        </div>
                    </div>
                </template>
            </CommonComponentsTimeLine>
        </div>
    </div>
</template>
<script lang="ts" setup>
    import { gsap } from 'gsap';
    import { ScrollTrigger } from 'gsap/all';
    // import { videoAutoPlay } from '~~/composables/useScrollTrigger.ts';
    import { matchMobile } from '~~/composables/useMatchRules.ts';
    gsap.registerPlugin(ScrollTrigger);

    defineProps<{
        timelineData: Array<{
            title: string;
            mediaPath: string;
            poster?: string;
        }>;
    }>();

    const emits = defineEmits<{
        (e: 'update:modelValue', index: number): void;
    }>();

    const curTimelineIndex = ref(0); // 当前高亮项

    watch(
        curTimelineIndex,
        (value) => {
            emits('update:modelValue', value);
        },
        { immediate: true },
    );

    onMounted(() => {
        const isMobile = matchMobile();
        const timelineDoms = gsap.utils.toArray('.timeline-item') as Array<HTMLElement>;
        // const timelineDoms = gsap.utils.toArray('.timeline-item > .fabric-timeline-video-elmt') as Array<HTMLVideoElement>,
        const timelineItems = timelineDoms.map((dom, index) => {
            const videoDom = dom.querySelector('.fabric-timeline-video-elmt') as HTMLVideoElement;
            return {
                index,
                dom,
                videoDom,
            };
        });

        if (!isMobile) {
            gsap.timeline({
                scrollTrigger: {
                    trigger: '.timeline-component-pc',
                    start: 'top center',
                    end: 'bottom center',
                    scrub: true,
                },
            })
                .set('.timeline-component-pc .timeline-progress-bar', { height: 0 })
                .to('.timeline-component-pc .timeline-progress-bar', {
                    height: '100%',
                    ease: 'none',
                });
            timelineItems.forEach((timelineItem) => {
                const itemRight = timelineItem.dom.querySelector('.timeline-right') as HTMLElement;

                const activate = () => {
                    curTimelineIndex.value = timelineItem.index;
                    timelineItem.videoDom.play();
                    gsap.to(itemRight, {
                        filter: 'opacity(1)',
                    });
                };

                const reset = () => {
                    timelineItem.videoDom.pause();
                    gsap.to(itemRight, {
                        filter: 'opacity(0.5)',
                    });
                };
                ScrollTrigger.create({
                    trigger: timelineItem.dom,
                    start: 'top center',
                    end: 'bottom center',
                    onEnter: () => activate(),
                    onEnterBack: () => activate(),
                    onLeave: () => reset(),
                    onLeaveBack: () => reset(),
                });
            });
        } else {
            // videoAutoPlay('.fabric-timeline-video-elm-mobile');
        }
    });
</script>

<style lang="less">
    .transition() {
        transition: all;
        transition: 0.5s;
    }
    .timeline-wrapper {
        z-index: 0;
        width: 100%;
        background: #000;
        position: relative;
        margin-bottom: .convert(100px) [ @vw];
        padding: 0 .convert(80px) [ @vw] 0 .convert(80px) [ @vw];

        .timeline-component-pc {
            display: flex;
            width: fit-content;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            margin-left: auto;
            margin-right: auto;
            position: relative;
            width: 100%;

            .timeline-progress {
                z-index: -3;
                width: 3px;
                height: 100%;
                background-color: #474747;
                position: absolute;
                left: calc(27647% / 1200 - 1.5px);

                .timeline-progress-bar {
                    z-index: -3;
                    will-change: height;
                    width: 3px;
                    background-color: #ffff00;
                    display: block;
                    position: absolute;
                    top: 0;
                }
            }

            .timeline-item {
                grid-column-gap: 0px;
                grid-row-gap: 0px;
                grid-template-rows: auto;
                grid-template-columns: 224fr .convert(100px) [ @vw] 864fr;
                grid-auto-columns: 1fr;
                display: grid;
                position: relative;
                width: 100%;

                &:nth-child(n + 3) {
                    margin-top: .convert(97px) [ @vw];
                }

                .sticky-block {
                    justify-content: flex-end;
                    align-items: flex-start;
                    display: flex;
                    position: -webkit-sticky;
                    position: sticky;
                    top: 50vh;
                }

                .timeline-left {
                    .timeline-title {
                        width: 100%;
                        text-align: left;

                        font-size: .convert(30px) [ @vw];
                        line-height: 1;
                        white-space: pre-line; //英文换行
                        color: #757575;

                        .transition();
                    }
                }

                .timeline-centre {
                    justify-content: center;
                    display: flex;

                    .timeline-circle {
                        width: 15px;
                        height: 15px;
                        max-height: 15px;
                        max-width: 15px;
                        min-height: 15px;
                        min-width: 15px;
                        border-radius: 100%;
                        background-color: #474747;

                        .transition();

                        height: .convert(56px) [ @vw];
                        justify-content: flex-end;
                        align-items: flex-start;
                        display: flex;
                        position: -webkit-sticky;
                        position: sticky;
                        top: 50vh;
                    }
                }

                .timeline-right {
                    width: .convert(869px) [ @vw];
                    height: .convert(488px) [ @vw];
                    filter: opacity(0.5);

                    video {
                        height: 100%;
                        width: 100%;
                    }
                }
            }

            .timeline-item-active {
                .timeline-title {
                    color: #fff;
                }

                .timeline-circle {
                    background-color: #fff;
                }
            }
        }
        .timeline-component-mobile {
            display: none;
            width: 100%;
            .default-timeline-progress-bar {
                background-color: #ffff00 !important;
            }
            .timeline-mobile-item {
                display: flex;
                flex-direction: column;
                gap: 10px;

                .timeline-mobile-item-title {
                    font-size: 16px;
                }
                .timeline-mobile-item-media {
                    width: .convert(300px) [ @vw-mobile];
                    video {
                        width: 100%;
                        height: auto;
                    }
                }
            }
        }

        @media (max-width: @screen-mobile-width) {
            padding: 0;
            .timeline-component-mobile {
                display: block;
            }
            .timeline-component-pc {
                display: none;
            }
        }
    }
</style>
